import React from 'react';
import { TESTIDS } from '@/lib/testIds';
import { Sparkles, BookOpen, MessageCircleHeart, Printer, NotebookPen, Library, Download } from 'lucide-react';

const resources = [
  {
    icon: Sparkles,
    title: 'Family Activities',
    body: 'Cozy, screen-free ways to explore feelings together — perfect for bedtime, breakfast, or the quiet in-between.',
    tag: 'Coming soon',
    color: '#B88ACB',
  },
  {
    icon: NotebookPen,
    title: 'Educator Resources',
    body: 'Classroom-ready lesson ideas, morning meeting prompts, and SEL activities grounded in real teaching experience.',
    color: '#F5C857',
    downloads: [
      {
        label: 'The Feelings Bench: Lesson Plan',
        href: '/downloads/The-Feelings-Bench-Lesson-Plan.pdf',
        filename: 'The-Feelings-Bench-Lesson-Plan.pdf',
      },
    ],
  },
  {
    icon: MessageCircleHeart,
    title: 'Discussion Questions',
    body: 'Gentle prompts that help children name what they feel and open meaningful conversations with grown-ups.',
    color: '#A8B78C',
    downloads: [
      {
        label: 'The Feelings Bench: Conversation Starters',
        href: '/downloads/Discussion-Prompts-and-Conversation-Starters.pdf',
        filename: 'Discussion-Prompts-and-Conversation-Starters.pdf',
      },
    ],
  },
  {
    icon: Printer,
    title: 'Printables',
    body: 'Free, beautifully-illustrated worksheets, feelings cards, and coloring pages for families and classrooms.',
    color: '#8D68B2',
    downloads: [
      {
        label: 'The Feelings Bench: Coloring Page',
        href: '/downloads/The-Feelings-Bench-Coloring-Page.pdf',
        filename: 'The-Feelings-Bench-Coloring-Page.pdf',
      },
    ],
  },
  {
    icon: BookOpen,
    title: 'Book Companion Guides',
    body: 'Deeper guides for each picture book with themes, vocabulary, and follow-along activities.',
    tag: 'Coming soon',
    color: '#B88ACB',
  },
  {
    icon: Library,
    title: 'Reading Lists',
    body: 'Curated picture-book stacks for every big feeling and every growing heart, updated with the seasons.',
    tag: 'Coming soon',
    color: '#F5C857',
  },
];

export default function Resources() {
  return (
    <section
      id="resources"
      data-testid={TESTIDS.resources.section}
      className="relative py-24 md:py-32 bg-[#FBF7F2]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto">
          <p className="font-accent text-2xl text-[#8D68B2]">For families &amp; educators</p>
          <h2 className="font-heading text-4xl md:text-5xl text-[#24385C] mt-2 leading-tight">
            A little garden of <span className="squiggle-underline">resources</span>
          </h2>
          <p className="mt-6 font-body text-lg text-[#24385C]/75">
            Thoughtful companions to the books — being planted now and blooming soon.
            Subscribe below to be the first to know when new resources are added.
          </p>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {resources.map((r, i) => {
            const Icon = r.icon;
            return (
              <div
                key={r.title}
                className="group relative bg-white rounded-[28px] p-7 border-2 border-white shadow-[0_10px_30px_-15px_rgba(36,56,92,0.15)] hover:shadow-[0_20px_45px_-15px_rgba(141,104,178,0.25)] transition-all duration-300 hover:-translate-y-1"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${r.color}22` }}
                >
                  <Icon size={26} style={{ color: r.color }} strokeWidth={1.8} />
                </div>
                <h3 className="font-heading text-2xl text-[#24385C] mt-5">{r.title}</h3>
                <p className="mt-3 font-body text-[15px] text-[#24385C]/75 leading-relaxed">{r.body}</p>

                {r.downloads && r.downloads.length > 0 ? (
                  <div className="mt-6 flex flex-col gap-2">
                    {r.downloads.map((d) => (
                      <a
                        key={d.href}
                        data-testid={`resource-download-${d.filename}`}
                        href={d.href}
                        download={d.filename}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#8D68B2] hover:bg-[#24385C] text-white rounded-full px-4 py-2 text-sm font-body font-semibold shadow-sm hover:shadow-md transition-all"
                      >
                        <Download size={14} strokeWidth={2} />
                        {d.label}
                      </a>
                    ))}
                    <span className="text-xs font-body text-[#24385C]/50 mt-1">
                      More printables blooming soon.
                    </span>
                  </div>
                ) : (
                  <div className="mt-6 inline-flex items-center gap-2 text-xs font-body font-semibold uppercase tracking-wider text-[#8D68B2] bg-[#FBE8EE] rounded-full px-3 py-1">
                    {r.tag}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
